import { WSInfoBox, WSList } from "@wingspanhq/fe-component-library";
import React from "react";
import { useHistory } from "react-router-dom";

import { queriesComplianceEntities, queriesPersons } from "@query";
import { useSession } from "@hooks/useSession";
import { useFocusedFeatures } from "../../../../../FocusedFeatures";
import { DataView } from "../../../../../shared/components/DataView";
import { normalizeDateOnly } from "../../../../../shared/utils/normalizeDateOnly";
import { useIsTaxDocumentsExist } from "../../../../TaxDocuments/utils/useIsTaxDocumentsExist";
import { usePayoutMethodMissingData } from "../../../components/AlertPayoutMethodMissingData";
import { DataViewTaxpayerInformation } from "../../../components/DataViewTaxpayerInformation";
import { alertMissingDataProps } from "../../../constants/alerts";
import { useModalEditAddress } from "../components/FormEditAddress";
import { useModalEditPreferredName } from "../components/FormEditPreferredName";
import { useModalEditWebsite } from "../components/FormEditWebsite";

type Props = {};

export const RoutePersonalInformation: React.FC<Props> = () => {
  const { personId } = useSession();
  const history = useHistory();
  const queryPerson = queriesPersons.getPerson.useQuery({ personId });
  const queryComplianceEntities =
    queriesComplianceEntities.listComplianceEntities.useQuery({});

  const modalEditPreferredName = useModalEditPreferredName();
  const modalEditWebsite = useModalEditWebsite();
  const modalEditAddress = useModalEditAddress();
  const focusedFeatures = useFocusedFeatures();
  const showOldTaxDocuments = useIsTaxDocumentsExist(2023);

  const payoutMethodMissingData = usePayoutMethodMissingData();

  if (!queryPerson.data || !queryComplianceEntities.data) {
    return null;
  }

  const person = queryPerson.data;
  const entity = queryComplianceEntities.data.data?.find(
    complianceEntity => !complianceEntity.subject
  );
  const address = entity?.physicalAddress;

  return (
    <WSList gap="2XL">
      <WSInfoBox title="Please ensure your information is up to date and accurate">
        <ol>
          <li>Click edit to edit your personal information.</li>
          <li>
            Your taxpayer information cannot be edited once your account has
            been verified (green checkmark).
          </li>
          <li>To make changes to verified fields, click “Request update”.</li>
          {showOldTaxDocuments ||
          focusedFeatures.showSideNavTaxDocumentsSettings ? (
            <li>
              To view your tax form delivery preferences, go to{" "}
              <a
                target="_self"
                onClick={() => history.push("/member/tax-documents")}
              >
                Tax Documents
              </a>
            </li>
          ) : null}
        </ol>
      </WSInfoBox>
      <DataViewTaxpayerInformation
        complianceEntity={entity}
        missingFields={payoutMethodMissingData?.requiredFields}
      />

      <DataView
        data={[
          {
            label: {
              text: "Preferred name",
              actionPosition: "left",
              action: {
                label: "Edit",
                onClick: () => {
                  modalEditPreferredName.open();
                }
              }
            },
            value: entity?.preferredName || undefined
          },
          {
            label: "Birthday",
            value: entity?.dateOfBirth
              ? normalizeDateOnly(entity.dateOfBirth)
              : undefined,
            fsExclude: true,
            formatterOptions: {
              dateType: "monthDayYear"
            }
          },
          {
            label: {
              text: "Business website",
              actionPosition: "left",
              action: {
                label: "Edit",
                onClick: () => {
                  modalEditWebsite.open();
                }
              }
            },
            value: entity?.websiteUrl || "None"
          },
          {
            label: {
              text: "Personal address",
              actionPosition: "left",
              action: {
                label: "Edit",
                onClick: () => {
                  modalEditAddress.open();
                }
              }
            },
            value: address
              ? [
                  address.line1,
                  address.line2,
                  [address.city, address.state, address.postalCode].join(" ")
                ]
                  .filter(Boolean)
                  .join("\n")
              : undefined,
            fsExclude: true,
            alert:
              payoutMethodMissingData?.requiredFields &&
              payoutMethodMissingData?.requiredFields.length > 0
                ? alertMissingDataProps
                : undefined
          },
          {
            label: "Contact info",
            value: [person.email, person.phone].filter(Boolean).join("\n")
          }
        ]}
        title="Personal information"
      />
    </WSList>
  );
};
